// src/pages/Admin/StudentCreate.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TitleWithBack from '../../components/common/TitleWithBack';
import { ProgressBar } from 'react-bootstrap';
import { downloadBulkUploadTemplate } from '../../utils/bulkUploadTemplates';

function StudentCreate() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('create'); // "create" or "bulk"
  const [tooltip, setTooltip] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // State for single student creation form
  const [form, setForm] = useState({
    studentName: '',
    email: '',
    program: '',
    examCenter: '',
    registrationNumber:'',
    yearOfStudy: '',
    semester: '',
    status: 'active'
  });
  const [uploadProgress, setUploadProgress] = useState(0);

  // State for bulk upload file
  const [uploadFile, setUploadFile] = useState(null);

  // Dropdown data for form selects
  const [programs, setPrograms] = useState([]);
  const [examCentres, setExamCentres] = useState([]);
  const [years, setYears] = useState([]);
  const [semesters, setSemesters] = useState([]);

  useEffect(() => {
    async function fetchDropdowns() {
      try {
        const [progRes, centreRes, yearRes, semRes] = await Promise.all([
          axios.get('/api/programs'),
          axios.get('/api/exam-centres'),
          axios.get('/api/years-of-study'),
          axios.get('/api/semesters')
        ]);
        setPrograms(Array.isArray(progRes.data) ? progRes.data : (progRes.data.data || []));
        setExamCentres(Array.isArray(centreRes.data) ? centreRes.data : (centreRes.data.data || []));
        setYears(Array.isArray(yearRes.data) ? yearRes.data : (yearRes.data.data || []));
        setSemesters(Array.isArray(semRes.data) ? semRes.data : (semRes.data.data || []));
      } catch (err) {
        console.error('Error fetching dropdown data', err);
      }
    }
    fetchDropdowns();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.studentName,
      email: form.email,
      program: { id: form.program },
      examCenter: { id: form.examCenter },
      registrationNumber: form.registrationNumber,
      yearOfStudy:{ id: form.yearOfStudy},
      semester: {id:form.semester},
      status: form.status
    };
    console.log('Payload being sent:', payload);
    try {
      await axios.post('/api/students', payload);
      setTooltip('Student created successfully!');
      setTimeout(() => navigate('/admin/students/view'), 2000);
    } catch (err) {
      setTooltip('Error creating student.');
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    const formData = new FormData();
    formData.append('file', uploadFile);
    setIsUploading(true);
    setUploadProgress(0);
    try {
      await axios.post('/api/bulk-upload/students', formData,{
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      setTooltip('Students uploaded successfully!');
      setTimeout(() => {
        setUploadProgress(0);
        setIsUploading(false); 
        navigate('/admin/students/view');
      }, 2000);
    } catch (err) {
      let backendMessage = 'Error uploading students.';
    if (err.response && err.response.data) {
      if (typeof err.response.data === 'string') {
        backendMessage = err.response.data;
      } else if (typeof err.response.data.message === 'string') {
        backendMessage = err.response.data.message;
      }
    }
    setTooltip(`Error: ${backendMessage}`);
    setIsUploading(false);
    }
  };

  return (
    <div className="container mt-2">
      <TitleWithBack title="Student Management" />
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'create' ? 'active' : ''}`} 
            onClick={() => setActiveTab('create')}
          >
            Create Student
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'bulk' ? 'active' : ''}`} 
            onClick={() => setActiveTab('bulk')}
          >
            Bulk Upload
          </button>
        </li>
      </ul>
      <div className="tab-content">
        {activeTab === 'create' && (
          <div className="tab-pane active">
            <form onSubmit={handleCreateSubmit}>
              <div className="mb-3">
                <label className="form-label">Student Name:</label>
                <input 
                  type="text" 
                  name="studentName" 
                  value={form.studentName} 
                  onChange={handleChange} 
                  className="form-control" 
                  required 
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Email:</label>
                <input 
                  type="email" 
                  name="email" 
                  value={form.email} 
                  onChange={handleChange} 
                  className="form-control" 
                  required 
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Program:</label>
                <select 
                  name="program" 
                  value={form.program} 
                  onChange={handleChange} 
                  className="form-select" 
                  required
                >
                  <option value="">Select Program</option>
                  {programs.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Exam Centre:</label>
                <select 
                  name="examCenter" 
                  value={form.examCenter} 
                  onChange={handleChange} 
                  className="form-select" 
                  required
                >
                  <option value="">Select Exam Centre</option>
                  {examCentres.map(ec => (
                    <option key={ec.id} value={ec.id}>{ec.centreName}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Registration Number:</label>
                <input 
                  type="text" 
                  name="registrationNumber" 
                  value={form.registrationNumber} 
                  onChange={handleChange} 
                  className="form-control"
                  required 
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Year of Study:</label>
                <select 
                  name="yearOfStudy" 
                  value={form.yearOfStudy} 
                  onChange={handleChange} 
                  className="form-select" 
                  required
                >
                  <option value="">Select Year</option>
                  {years.map(y => (
                    <option key={y.id} value={y.id}>{y.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Semester:</label>
                <select 
                  name="semester" 
                  value={form.semester} 
                  onChange={handleChange} 
                  className="form-select" 
                  required
                >
                  <option value="">Select Semester</option>
                  {semesters.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Status:</label>
                <select 
                  name="status" 
                  value={form.status} 
                  onChange={handleChange} 
                  className="form-select" 
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary">Create Student</button>
            </form>
          </div>
        )}
        {activeTab === 'bulk' && (
          <div className="tab-pane active">
            <form onSubmit={handleBulkSubmit}>
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => downloadBulkUploadTemplate('students')}
                >
                  Download Template
                </button>
              </div>
              <div className="mb-3">
                <label className="form-label">Excel File:</label>
                <input 
                  type="file" 
                  accept=".xls,.xlsx" 
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setUploadFile(file);
                      setUploadProgress(0); 
                      setIsUploading(false);
                    }
                  }}                   
                  className="form-control" 
                />
              {isUploading && (
              <div className="my-3">
               <ProgressBar 
                now={uploadProgress} 
                label={`${uploadProgress}%`} 
                animated 
                variant={uploadProgress < 100 ? 'info' : 'success'} 
              />
             </div>
             )}
              </div>
              <button type="submit" className="btn btn-primary">Upload Students</button>
            </form>
          </div>
        )}
      </div>
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
    </div>
  );
}

export default StudentCreate;
